import React from "react";
import { Play, Users, Clock, Trophy } from "lucide-react";

const TrainingSection = () => {
  const trainingTiers = [
    {
      tier: "Starter",
      icon: Play,
      title: "Training Video Library",
      description: "Self-paced learning with comprehensive video tutorials",
      features: [
        "20+ HD training videos",
        "Step-by-step system walkthroughs", 
        "Best practices guides",
        "24/7 access to training portal",
        "Progress tracking"
      ],
      color: "var(--accent-blue-200)",
      accentColor: "var(--accent-blue-400)"
    },
    {
      tier: "Professional", 
      icon: Users,
      title: "Live Training + Train-the-Trainer",
      description: "Hands-on training with your team plus internal capability building",
      features: [
        "1 full day live training session",
        "2-hour train-the-trainer program",
        "Custom training materials",
        "Team certification process",
        "90-day follow-up support"
      ],
      color: "var(--accent-orange-200)",
      accentColor: "var(--accent-orange-400)"
    },
    {
      tier: "Enterprise",
      icon: Trophy,
      title: "Comprehensive Training Program",
      description: "Multi-day intensive training with advanced train-the-trainer certification",
      features: [
        "Up to 3 days on-site training",
        "4-hour comprehensive train-the-trainer",
        "Custom curriculum development",
        "Multi-department training",
        "Ongoing training support",
        "Advanced certification program"
      ],
      color: "var(--accent-purple-200)",
      accentColor: "var(--accent-purple-400)"
    }
  ];

  return (
    <section className="py-20" style={{ background: 'var(--bg-section)' }}>
      <div className="container">
        <div className="text-center mb-16">
          <div className="hero-announcement mb-6">
            <Clock size={12} style={{ color: 'var(--accent-orange-400)' }} />
            <span>TRAINING THAT GUARANTEES SUCCESS</span>
          </div>
          <h2 className="heading-1 mb-4">
            Master Your AI System with 
            <span style={{ color: 'var(--accent-orange-400)' }}> Expert Training</span>
          </h2>
          <p className="body-large" style={{ 
            maxWidth: '700px', 
            margin: '0 auto',
            color: 'var(--text-secondary)' 
          }}>
            Don't just get an AI system—master it. Our comprehensive training ensures your team 
            can maximize ROI and become internal AI experts.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {trainingTiers.map((tier, index) => {
            const IconComponent = tier.icon;
            
            return (
              <div 
                key={index}
                className="voice-card hover-lift relative overflow-hidden"
                style={{ background: tier.color }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div 
                    className="p-3 rounded-full flex-shrink-0"
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.8)',
                      color: tier.accentColor
                    }}
                  >
                    <IconComponent size={24} />
                  </div>
                  <div className="flex-1">
                    <div className="caption mb-1" style={{ color: tier.accentColor }}>
                      {tier.tier} Plan
                    </div>
                    <h3 className="voice-card-title mb-2">
                      {tier.title}
                    </h3>
                  </div>
                </div>
                
                <div className="voice-card-description mb-4">
                  {tier.description}
                </div>

                <div className="mb-6">
                  <div className="body-small font-semibold mb-3">✓ What's Included:</div>
                  <ul className="space-y-2">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-2">
                        <div 
                          className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                          style={{ background: tier.accentColor }}
                        />
                        <span className="caption">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div 
                  className="p-3 rounded-lg text-center"
                  style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                >
                  <div className="caption font-semibold" style={{ color: tier.accentColor }}>
                    Included in {tier.tier} Plan
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Training Benefits */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center gap-4 p-8 rounded-lg max-w-2xl mx-auto" style={{ 
            background: 'var(--bg-card)',
            border: '2px solid var(--accent-orange-400)'
          }}>
            <h3 className="heading-2 mb-2" style={{ color: 'var(--accent-orange-400)' }}>
              Training Ensures Success
            </h3>
            <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
              Comprehensive training helps your team maximize the value of your AI system and achieve faster adoption.
            </p>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="heading-3 mb-1" style={{ color: 'var(--accent-green-200)' }}>Video</div>
                <div className="caption">Self-Paced Learning</div>
              </div>
              <div className="text-center">
                <div className="heading-3 mb-1" style={{ color: 'var(--accent-blue-400)' }}>Live</div>
                <div className="caption">Hands-On Sessions</div>
              </div>
              <div className="text-center">
                <div className="heading-3 mb-1" style={{ color: 'var(--accent-purple-400)' }}>Train</div>
                <div className="caption">Internal Experts</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrainingSection;